import { collection, addDoc, Timestamp, query, where, orderBy, limit, getDocs, QueryConstraint } from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { AuditLog, CreateAuditLogParams, AuditLogSeverity } from '../../types/auditLog';

/**
 * Default severity mapping for event types
 */
const DEFAULT_SEVERITY_MAP: Record<string, AuditLogSeverity> = {
  // Critical events
  'unauthorized_access_attempt': 'critical',
  'security_rule_violation': 'critical',
  'suspicious_activity': 'critical',
  'account_locked': 'critical',
  'data_deleted': 'warning',
  'data_deleted_for_privacy_request': 'info',
  
  // Warning events
  'failed_login': 'warning',
  'permission_denied': 'warning',
  'role_changed': 'warning',
  'user_deleted': 'warning',
  
  // Info events (default)
  'user_login': 'info',
  'user_logout': 'info',
  'user_signup': 'info',
  'data_viewed': 'info',
  'data_created': 'info',
  'data_updated': 'info',
  'sensitive_data_accessed': 'info',
};

/**
 * Creates an audit log entry
 */
export async function createAuditLog(params: CreateAuditLogParams): Promise<string> {
  try {
    // Determine severity if not provided
    const severity: AuditLogSeverity = params.severity || 
      DEFAULT_SEVERITY_MAP[params.eventType] || 
      'info';

    // Get IP address and user agent from browser if available
    const ipAddress = params.ipAddress || (typeof window !== 'undefined' ? 
      (window as any).clientIP : undefined);
    const userAgent = params.userAgent || (typeof window !== 'undefined' ? 
      navigator.userAgent : undefined);

    const auditLog: Omit<AuditLog, 'id'> = {
      userId: params.userId,
      userEmail: params.userEmail,
      userRole: params.userRole,
      eventType: params.eventType,
      severity,
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      description: params.description,
      metadata: params.metadata || {},
      ipAddress,
      userAgent,
      timestamp: Timestamp.now(),
      success: params.success !== undefined ? params.success : true,
      errorMessage: params.errorMessage,
    };

    const docRef = await addDoc(collection(db, 'auditLogs'), auditLog);
    return docRef.id;
  } catch (error: any) {
    // Log error but don't throw - audit logging should not break the application
    console.error('Failed to create audit log:', error);
    // Optionally, you could send to an external logging service here
    return '';
  }
}

/**
 * Creates an audit log entry for authentication events
 */
export async function logAuthenticationEvent(
  userId: string,
  userEmail: string,
  userRole: 'admin' | 'staff' | 'client',
  eventType: 'user_login' | 'user_logout' | 'user_signup' | 'failed_login' | 'password_reset_request' | 'password_reset_complete',
  success: boolean = true,
  errorMessage?: string
): Promise<void> {
  const descriptions: Record<string, string> = {
    'user_login': `User logged in: ${userEmail}`,
    'user_logout': `User logged out: ${userEmail}`,
    'user_signup': `New user signed up: ${userEmail}`,
    'failed_login': `Failed login attempt: ${userEmail}`,
    'password_reset_request': `Password reset requested: ${userEmail}`,
    'password_reset_complete': `Password reset completed: ${userEmail}`,
  };

  await createAuditLog({
    userId,
    userEmail,
    userRole,
    eventType,
    resourceType: 'user',
    resourceId: userId,
    description: descriptions[eventType] || `Authentication event: ${eventType}`,
    success,
    errorMessage,
  });
}

/**
 * Creates an audit log entry for data access events
 */
export async function logDataAccess(
  userId: string,
  userEmail: string,
  userRole: 'admin' | 'staff' | 'client',
  resourceType: AuditLog['resourceType'],
  resourceId: string,
  description: string,
  isSensitive: boolean = false
): Promise<void> {
  await createAuditLog({
    userId,
    userEmail,
    userRole,
    eventType: isSensitive ? 'sensitive_data_accessed' : 'data_viewed',
    severity: isSensitive ? 'warning' : 'info',
    resourceType,
    resourceId,
    description,
  });
}

/**
 * Creates an audit log entry for data modification events
 */
export async function logDataModification(
  userId: string,
  userEmail: string,
  userRole: 'admin' | 'staff' | 'client',
  eventType: 'data_created' | 'data_updated' | 'data_deleted' | 'task_created' | 'task_updated' | 'task_deleted' | 'user_created' | 'user_updated' | 'user_deleted',
  resourceType: AuditLog['resourceType'],
  resourceId: string,
  description: string,
  metadata?: Record<string, any>
): Promise<void> {
  const severity = (eventType === 'data_deleted' || eventType === 'task_deleted' || eventType === 'user_deleted') ? 'warning' : 'info';
  await createAuditLog({
    userId,
    userEmail,
    userRole,
    eventType: eventType as AuditLog['eventType'],
    severity,
    resourceType,
    resourceId,
    description,
    metadata,
  });
}

/**
 * Creates an audit log entry for security events
 */
export async function logSecurityEvent(
  userId: string,
  userEmail: string,
  userRole: 'admin' | 'staff' | 'client',
  eventType: 'permission_denied' | 'unauthorized_access_attempt' | 'security_rule_violation' | 'suspicious_activity',
  description: string,
  resourceType?: AuditLog['resourceType'],
  resourceId?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await createAuditLog({
    userId,
    userEmail,
    userRole,
    eventType,
    severity: 'critical',
    resourceType: resourceType || 'system',
    resourceId,
    description,
    metadata,
  });
}

/**
 * Creates an audit log entry for privacy/compliance events
 */
export async function logPrivacyEvent(
  userId: string,
  userEmail: string,
  userRole: 'admin' | 'staff' | 'client',
  eventType: AuditLog['eventType'],
  resourceType: AuditLog['resourceType'],
  resourceId: string,
  description: string,
  metadata?: Record<string, any>
): Promise<void> {
  await createAuditLog({
    userId,
    userEmail,
    userRole,
    eventType,
    severity: 'info',
    resourceType,
    resourceId,
    description,
    metadata,
  });
}

/**
 * Retrieves audit logs with optional filtering
 */
export async function getAuditLogs(
  filters?: {
    userId?: string;
    eventType?: AuditLog['eventType'];
    resourceType?: AuditLog['resourceType'];
    resourceId?: string;
    severity?: AuditLog['severity'];
    startDate?: Date;
    endDate?: Date;
    limitCount?: number;
  }
): Promise<AuditLog[]> {
  try {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters?.eventType) {
      constraints.push(where('eventType', '==', filters.eventType));
    }
    if (filters?.resourceType) {
      constraints.push(where('resourceType', '==', filters.resourceType));
    }
    if (filters?.resourceId) {
      constraints.push(where('resourceId', '==', filters.resourceId));
    }
    if (filters?.severity) {
      constraints.push(where('severity', '==', filters.severity));
    }
    if (filters?.startDate) {
      constraints.push(where('timestamp', '>=', Timestamp.fromDate(filters.startDate)));
    }
    if (filters?.endDate) {
      constraints.push(where('timestamp', '<=', Timestamp.fromDate(filters.endDate)));
    }
    
    constraints.push(orderBy('timestamp', 'desc'));
    
    if (filters?.limitCount) {
      constraints.push(limit(filters.limitCount));
    } else {
      constraints.push(limit(1000)); // Default limit
    }
    
    const q = query(collection(db, 'auditLogs'), ...constraints);
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as AuditLog[];
  } catch (error: any) {
    console.error('Failed to retrieve audit logs:', error);
    throw error;
  }
}
